import type {
  UserCreateRequest,
  UserDeleteRequest,
  UserEditRequest,
} from 'shared/domain/messages/user/eventMessages';
import { broadcastAllUsers } from 'serviceWorker/broadcasts/user/all';
import * as users from 'serviceWorker/db/users/users';
import * as usersService from 'serviceWorker/db/users/usersService';
import { debugLog } from 'shared/logger/debugLog';
import { Services } from 'shared/domain/messages/message';
import { userContract } from 'shared/domain/user/userContract';
import { createSyncedEntity } from '../factories/createSyncedEntity';
import { userSaver } from './addUser';
import { userDeleter } from './deleteUser';
import { userEditor } from './editUser';
import { fetchUsers } from './fetchUsers';

/**
 * Download of the users; the source's `sync` is its entry point. The users
 * have no sync key, so every sync downloads the whole list again (as the
 * legacy forced PULL did); the stored users stay readable while it runs.
 */
export const usersSync = createSyncedEntity({
  entityName: Services.USERS,
  contract: userContract,
  repository: users,
  service: usersService,
  fetch: fetchUsers,
  publishCollection: broadcastAllUsers,
  requiredConfig: ['api', 'projectId'],
  keepStoredWhileDownloading: true,
});

/**
 * Resolves once the change is in the HTTP queue and rejects only when it could
 * not be queued. The server's answer follows as an event (`uploadedEntity`,
 * `failUploadedEntity`, …) with the change's `uniqueId`.
 */
export function handleCreateUser(detail: UserCreateRequest): Promise<void> {
  debugLog('addUser', detail);
  return userSaver.execute(detail.userCreateModel, detail.uniqueId);
}

export function handleEditUser(detail: UserEditRequest): Promise<void> {
  debugLog('editUser', detail);
  return userEditor.execute(detail.userEditModel, detail.uniqueId);
}

export function handleDeleteUser(detail: UserDeleteRequest): Promise<void> {
  debugLog('deleteUser', detail);
  return userDeleter.execute(detail.userDeleteModel, detail.uniqueId);
}
